/* global FileReader */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Papa from 'papaparse';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box,
  Table, TableHead, TableBody, TableRow, TableCell, Checkbox, Chip,
} from '@mui/material';
import buildAudiencePreviewRows, { normalizeLineEndings } from '../../utils/audienceCsv';

const STATUS_CHIP = {
  new: { label: 'New', color: 'success' },
  overwrite: { label: 'Overwrites existing', color: 'warning' },
  duplicate: { label: 'Duplicate in file', color: 'warning' },
  invalid: { label: 'Invalid', color: 'error' },
};

const DUPLICATE_HINT = 'If more than one row with this abbreviation stays checked, the last checked one (in file order) is the one that gets saved.';

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

function UploadAudiencesModal({
  open, onClose, existingAudiences, onConfirm,
}) {
  const [rows, setRows] = useState([]);
  const [fileError, setFileError] = useState('');

  const isPreview = rows.length > 0;

  const resetAndClose = () => {
    setRows([]);
    setFileError('');
    onClose();
  };

  const chooseDifferentFile = () => {
    setRows([]);
    setFileError('');
  };

  const handleFileChange = async (event) => {
    const { target } = event;
    const file = target.files[0];
    target.value = '';
    if (!file) return;

    if (!/\.csv$/i.test(file.name || '')) {
      setFileError('Only CSV files are supported.');
      return;
    }

    let text;
    try {
      text = normalizeLineEndings(await readFileAsText(file));
    } catch {
      setFileError('Could not read this file. Please make sure it is a valid CSV.');
      return;
    }

    Papa.parse(text, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        // skip any row level errors
        const fileLevelErrors = results.errors.filter((err) => typeof err.row !== 'number');
        if (fileLevelErrors.length > 0) {
          setFileError('Could not read this file. Please make sure it is a valid CSV.');
          return;
        }
        if (!results.data || results.data.length === 0) {
          setFileError('This file has no data rows.');
          return;
        }
        const firstRow = results.data[0];
        const hasRequiredHeaders = Object.prototype.hasOwnProperty.call(firstRow, 'Abbreviation')
           && Object.prototype.hasOwnProperty.call(firstRow, 'Full Name');
        if (!hasRequiredHeaders) {
          setFileError('CSV must include "Full Name" and "Abbreviation" headers.');
          return;
        }
        const rowErrorReasons = {};
        results.errors.forEach((err) => {
          rowErrorReasons[err.row] = 'This row has an unexpected number of columns - check for a misplaced comma or line break.';
        });
        const previewRows = buildAudiencePreviewRows(results.data, existingAudiences).map(
          (row, index) => (rowErrorReasons[index]
            ? {
              ...row, status: 'invalid', checked: false, reason: rowErrorReasons[index],
            }
            : row),
        );
        if (previewRows.length === 0) {
          setFileError('This file has no data rows.');
          return;
        }
        setFileError('');
        setRows(previewRows.map((row, index) => ({ ...row, id: index })));
      },
    });
  };

  const toggleRow = (id) => {
    setRows(
      (prevRows) => prevRows.map(
        (row) => (row.id === id ? { ...row, checked: !row.checked } : row),
      ),
    );
  };

  const handleConfirm = () => {
    const mergedValues = { ...existingAudiences };
    rows.forEach((row) => {
      if (row.checked) {
        mergedValues[row.code] = row.label;
      }
    });
    onConfirm(mergedValues);
    resetAndClose();
  };

  const summary = ['new', 'overwrite', 'duplicate', 'invalid']
    .map((status) => ({ status, count: rows.filter((row) => row.status === status).length }))
    .filter(({ count }) => count > 0);

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload Audiences from CSV</DialogTitle>
      <DialogContent>
        {!isPreview && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a CSV with &quot;Full Name&quot; and &quot;Abbreviation&quot; columns.
            </Typography>
            <Button variant="outlined" component="label">
              Choose File
              <input
                type="file"
                accept=".csv"
                hidden
                aria-label="Upload CSV file"
                onChange={handleFileChange}
              />
            </Button>
            {fileError && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                {fileError}
              </Typography>
            )}
          </Box>
        )}
        {isPreview && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {summary.map(({ status, count }) => `${count} ${STATUS_CHIP[status].label.toLowerCase()}`).join(' · ')}
            </Typography>
            <Button size="small" onClick={chooseDifferentFile} sx={{ mb: 1 }}>
              Choose a different file
            </Button>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Abbreviation</TableCell>
                  <TableCell>Full Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={row.checked}
                        disabled={row.status === 'invalid'}
                        onChange={() => toggleRow(row.id)}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{row.code || '—'}</TableCell>
                    <TableCell>{row.label || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={STATUS_CHIP[row.status].label}
                        color={STATUS_CHIP[row.status].color}
                      />
                    </TableCell>
                    <TableCell>
                      {(row.status === 'duplicate' ? DUPLICATE_HINT : row.reason) || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose}>Cancel</Button>
        {isPreview && (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!rows.some((row) => row.checked)}
          >
            Confirm
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

UploadAudiencesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  existingAudiences: PropTypes.objectOf(PropTypes.string).isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default UploadAudiencesModal;
